// NEW_FILE_JUSTIFIED: Review form state for ImportEmployeeDialog — extracted to stay under 2-useState-per-component limit
import { useState, useCallback } from 'react';
import type { IEmployeeDocument, TDocumentSlot, IImportEmployeeReviewFormState } from '@/@types/company';

const emptyAssignments: Record<TDocumentSlot, IEmployeeDocument | undefined> = {
  identityDocFront: undefined,
  identityDocBack: undefined,
  drivingLicenseFront: undefined,
  drivingLicenseBack: undefined,
};

const initialFormState: IImportEmployeeReviewFormState = {
  name: '',
  dateOfBirth: '',
  identityDocumentNumber: '',
  nationality: '',
  city: '',
  postalCode: '',
  phoneNumber: '',
  email: '',
  documentAssignments: { ...emptyAssignments },
  pendingFiles: [],
};

export function useImportEmployeeReviewForm() {
  const [form, setForm] = useState<IImportEmployeeReviewFormState>(initialFormState);

  const setField = useCallback(
    (field: keyof Omit<IImportEmployeeReviewFormState, 'documentAssignments' | 'pendingFiles'>, value: string) => {
      setForm(prev => ({ ...prev, [field]: value }));
    },
    []
  );

  const setDocumentSlot = useCallback(
    (slot: TDocumentSlot, doc: IEmployeeDocument | undefined) => {
      setForm(prev => ({
        ...prev,
        documentAssignments: { ...prev.documentAssignments, [slot]: doc },
      }));
    },
    []
  );

  const addFiles = useCallback((files: File[]) => {
    setForm(prev => {
      const existingNames = new Set(prev.pendingFiles.map(f => f.name));
      const newFiles = files.filter(f => !existingNames.has(f.name));
      return { ...prev, pendingFiles: [...prev.pendingFiles, ...newFiles] };
    });
  }, []);

  const removeFile = useCallback((index: number) => {
    setForm(prev => ({
      ...prev,
      pendingFiles: prev.pendingFiles.filter((_, i) => i !== index),
    }));
  }, []);

  const resetForm = useCallback(() => {
    setForm(initialFormState);
  }, []);

  return {
    ...form,
    setField,
    setDocumentSlot,
    addFiles,
    removeFile,
    resetForm,
  };
}
